/**
 * ValidationFeedback Component
 *
 * Displays real-time validation results for the label layout.
 * Shows a condensed summary in the side panel with warnings.
 * Errors are promoted to the top-level banner in LabelLayoutEditor.
 */

import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLabelLayoutStore } from './label-layout-store';
import { validateScannability, COMPONENT_LABEL_KEYS } from './label-layout-types';

export function ValidationFeedback() {
  const { t } = useTranslation('settings');
  const components = useLabelLayoutStore((s) => s.components);
  const canvasSize = useLabelLayoutStore((s) => s.canvasSize);
  const labelType = useLabelLayoutStore((s) => s.labelType);

  const validation = validateScannability(components, canvasSize, labelType);

  // Show success state when no issues
  if (validation.valid && validation.warnings.length === 0) {
    return (
      <div
        className="flex items-center gap-2 p-2 rounded-md bg-green-50 border border-green-200"
        role="status"
        aria-live="polite"
      >
        <CheckCircle className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />
        <span className="text-green-800 text-xs">{t('production.validation.layoutValid')}</span>
      </div>
    );
  }

  // Show condensed summary - errors appear in top banner, only show warnings here
  return (
    <div
      className="space-y-2"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`Validation: ${validation.errors.length} errors, ${validation.warnings.length} warnings`}
    >
      {/* Error count indicator (details in top banner) */}
      {validation.errors.length > 0 && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
          <span className="font-medium">{t('production.validation.errorsCount', { count: validation.errors.length })}</span>
          <span className="text-muted-foreground">{t('production.validation.seeBannerAbove')}</span>
        </div>
      )}
      {/* Warnings shown inline - translated by code */}
      {validation.structuredWarnings.map((warning, i) => {
        const translationKey = `production.validation.codes.${warning.code}`;
        const componentName = warning.componentId
          ? t(COMPONENT_LABEL_KEYS[components.find(c => c.id === warning.componentId)?.type ?? 'qr'])
          : undefined;
        const translated = t(translationKey, {
          componentName,
          labelType,
          defaultValue: warning.message,
        });
        return (
          <Alert key={`warning-${i}`} className="border-amber-200 bg-amber-50 py-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
            <AlertDescription className="text-amber-800 text-xs">{translated}</AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
